import { Stack, Field, ErrorMessage, Button, IconButton, InfoCard, Select, Title, useActions } from "@introist/react-foundation/v2";
import { Block, TextInput } from "components/atoms";
import { MessageEditor as MessageEditorV4 } from "components/editors/v4/MessageEditor/MessageEditor";
import { AttachmentsBlock } from "modules/workflows/blocks/Attachments";
import { StepEditorAccordion } from "modules/workflows/routes/WorkflowEditor/StepEditorAccordion";
import { useCallback, useMemo, useState } from "react";
import { prefixForm } from "utils/FormUtils";
import { StepEditorProps } from "../../useStepEditor";
import { EmailV2AddressBlock } from "./EmailV2AddressBlock";
import { ConditionalGroupJson } from "@introist/introist-commons/dist";
import { useEmployeeField } from "modules/employees/hooks/useEmployeeFields";
import { get, isNil } from "lodash";
import { TagSelector } from "../../../../system/tags/TagSelector";
import { Tag } from "modules/system/tags/useTags";
import { EmailSettings } from "../EmailSettings";

type LangValue = { subject: string; message: string };

const emptyLangValue = (): LangValue => ({ subject: "", message: "" });

const getLangValue = (langs: ConditionalGroupJson, lang: string): LangValue | undefined => {
  if (lang === "default") {
    return isNil(langs.defaultValue) ? undefined : (langs.defaultValue as LangValue);
  }
  const item = langs.items?.find(i => i.id === lang);
  return item ? (item.value as LangValue) : undefined;
};

const setLangValue = (
  langs: ConditionalGroupJson,
  lang: string,
  value: LangValue
): ConditionalGroupJson => {
  if (lang === "default") {
    return { ...langs, defaultValue: value };
  }
  return {
    ...langs,
    items: langs.items?.map(i => (i.id === lang ? { ...i, value } : i))
  };
};

export const EmailV2StepEditor = ({ form, stepDataPath, workflowId }: StepEditorProps) => {
  const prefixedForm = prefixForm(form, stepDataPath);
  const { onConfirmAction } = useActions();

  const { field } = useEmployeeField("language");
  const langs = useMemo(() => ["default", ...(field?.options ?? [])], [field]);

  const langsValue: ConditionalGroupJson = useMemo(
    () => prefixedForm.get("langs") ?? { items: [], defaultValue: emptyLangValue() },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prefixedForm.get("langs")]
  );

  const [selectedLang, setSelectedLang] = useState(
    langsValue.defaultValue ? "default" : get(langsValue, "items[0].id") ?? "default"
  );

  const onChangeLangs = useCallback(
    (updated: ConditionalGroupJson) => {
      prefixedForm.set("langs")(updated);
    },
    [prefixedForm]
  );

  const onCreate = useCallback(() => {
    if (selectedLang === "default") {
      onChangeLangs({ ...langsValue, defaultValue: emptyLangValue() });
    } else {
      onChangeLangs({
        ...langsValue,
        items: [
          ...(langsValue.items ?? []),
          {
            id: selectedLang,
            title: selectedLang,
            condition: { and: [{ language: { eq: selectedLang } }] },
            value: emptyLangValue()
          }
        ]
      });
    }
  }, [langsValue, selectedLang, onChangeLangs]);

  const onRemove = onConfirmAction(
    (itemId: string) => {
      if (itemId === "default") {
        onChangeLangs({ ...langsValue, defaultValue: null });
      } else {
        const items = langsValue.items?.filter(i => i.id !== itemId);
        onChangeLangs({ ...langsValue, items });
      }
    },
    {
      title: "Remove language version",
      description: "Are you sure you want to remove this language version?"
    }
  );

  const langSelect = (itemId?: string) => {
    if (langs.length === 1) return null;

    return (
      <Stack gap="xSmall" alignItems="center">
        <Title>Language:</Title>
        <Select
          element="button"
          variant="blended"
          size="small"
          options={langs.map(lang => ({ key: lang, title: lang === "default" ? "Default" : lang }))}
          value={selectedLang}
          onSelect={opt => setSelectedLang(opt.key)}
          attachToRef={false}
        />
        {itemId && <IconButton icon="trash" onClick={() => onRemove(itemId)} />}
      </Stack>
    );
  };

  const renderLangContent = () => {
    const langValue = getLangValue(langsValue, selectedLang);

    if (langValue !== undefined) {
      const onChangeSubject = (subject: string) => {
        onChangeLangs(setLangValue(langsValue, selectedLang, { ...langValue, subject }));
      };
      const onChangeMessage = (message: string) => {
        onChangeLangs(setLangValue(langsValue, selectedLang, { ...langValue, message }));
      };

      return (
        <Stack vertical gap="xLarge">
          {langSelect(selectedLang)}
          <Field title="Subject">
            <TextInput
              size="small"
              placeholder="Email subject"
              value={langValue.subject ?? ""}
              endAdornment={
                <TagSelector
                  title=""
                  size="small"
                  onSelect={(tag: Tag) => onChangeSubject(`${langValue.subject ?? ""}{{${tag.tag}}}`)}
                  ctx={{ workflowId }}
                />
              }
              onChange={onChangeSubject}
            />
          </Field>
          <Field title="Message">
            <MessageEditorV4
              message={langValue.message ?? ""}
              onChange={onChangeMessage}
              workflowId={workflowId}
            />
          </Field>
        </Stack>
      );
    }

    return (
      <Stack vertical style={{ height: "290px" }} alignItems="center" justifyContent="center">
        <Stack gap="xSmall">
          <Title>No message for language</Title>
          {langSelect()}
        </Stack>
        {selectedLang === "default" && (
          <InfoCard title="Default message" colorVariant="primary" style={{ width: "400px" }}>
            <Title>Default message is used if the right language version is not found.</Title>
          </InfoCard>
        )}
        <Button style={{ margin: "0 auto" }} variant="outlined" onClick={onCreate}>
          {selectedLang === "default" ? "Create default message" : "Create language version"}
        </Button>
      </Stack>
    );
  };

  const hasError = prefixedForm.isError("langs");

  return (
    <>
      <EmailV2AddressBlock form={form} formKeyPrefix={stepDataPath} />
      <Block compact blended>
        <StepEditorAccordion
          error={hasError ? "Invalid fields" : undefined}
          title="Message"
          defaultOpen={true}
        >
          {renderLangContent()}
          {prefixedForm.error("langs") && (
            <ErrorMessage>{prefixedForm.error("langs")}</ErrorMessage>
          )}
        </StepEditorAccordion>
      </Block>
      <AttachmentsBlock form={form} />
      <EmailSettings form={form} stepDataPath={stepDataPath} />
    </>
  );
};
